import { CalendarModeRangeType, TimeInterval } from "@custom-types/Types";
import {
  AnalyticsDataResponse,
  AnalyticQueueDataResponse
} from "@queries/Analytics";
import { Theme } from "@themes/Types";
import {
  chartTooltipFormatter,
  getAxisIntervalLabels,
  numberSymbol,
  xAxisLabelFormatter
} from "@utils/Widget";
import {
  EChartsOption,
  SeriesOption,
  TooltipComponentFormatterCallbackParams
} from "echarts";
import { isArray } from "lodash";
import { theme as HVTheme } from "@repo/HVComponents";
import { commonTooltipSettings } from "@components/report/widget/widget-statistics/helper";
import { formatTime } from "@utils/Global";
import { renderToString } from "react-dom/server";
import {
  ColorDot,
  ToolTipTitle
} from "@components/widgets/retail-visitor-area-traffic/widget/RetailVisitorAreaTraffic.styled";
import { BaseTimeProps } from "./ChartCard/Interfaces";

const tooltipSettingLine = (
  timeInterval: TimeInterval,
  mode: CalendarModeRangeType,
  isTime?: boolean
): EChartsOption["tooltip"] => ({
  trigger: "axis",
  confine: true,
  formatter: (params: TooltipComponentFormatterCallbackParams) => {
    if (isArray(params)) {
      return chartTooltipFormatter(
        timeInterval,
        params[0].name,
        params.map((item) => {
          return {
            label: item.seriesName || "",
            data: String(item.data),
            color: String(item.color)
          };
        }),
        "",
        mode,
        isTime ? 0 : 1,
        true
      );
    }
    return "";
  },
  ...commonTooltipSettings()
});

export const chartPeopeleObjectType = (
  theme: Theme,
  timeInterval: TimeInterval,
  activeLocations: Set<string>,
  baseTime: BaseTimeProps,
  viewType: string,
  analyticsData: AnalyticsDataResponse[],
  timeZone?: string
): EChartsOption => {
  const { mode, start, end } = baseTime;
  const result = getAxisIntervalLabels(
    mode,
    start,
    end,
    timeInterval,
    timeZone
  );
  const { labels: xAxisLabels } = result;

  const generateSeries = (): SeriesOption[] => {
    return analyticsData.reduce<SeriesOption[]>((acc, item) => {
      if (!activeLocations.has(item.key)) {
        acc.push({
          name: item.locationName,
          type: "line",
          showSymbol: item.chartData.length === 1,
          data: item.chartData,
          color: item.color
        });
      }
      return acc;
    }, []);
  };

  return {
    tooltip: tooltipSettingLine(
      timeInterval,
      mode,
      viewType === "wait_time" || viewType === "waittime"
    ),
    legend: {
      show: false
    },
    grid: {
      left: 0,
      right: 15,
      top: 10,
      bottom: 0,
      containLabel: true,
      show: true,
      borderColor: "transparent"
    },
    toolbox: {
      show: false
    },
    dataZoom: [
      {
        type: "inside"
      }
    ],
    xAxis: [
      {
        type: "category",
        nameTextStyle: {
          color: theme.chart.colorNewAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          verticalAlign: "top",
          lineHeight: 25,
          padding: [1, 0, 0, 0]
        },
        nameGap: 11,
        data: xAxisLabels,
        boundaryGap: false,
        axisLabel: {
          color: theme.chart.colorNewAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          formatter: xAxisLabelFormatter(timeInterval, mode)
        },
        axisLine: {
          show: false
        },
        axisTick: {
          lineStyle: {
            color: theme.widget.stoppedVehicleStatistics.graphLineColor,
            width: 1
          }
        }
      }
    ],
    yAxis: {
      type: "value",
      nameTextStyle: {
        color: theme.chart.colorNewAxisLabel,
        fontSize: "12px",
        fontFamily: HVTheme.fontFamily,
        align: "right",
        padding: [0, 6, 0, 0]
      },
      nameGap: 12,
      splitNumber: 2,
      axisLabel: {
        color: theme.chart.colorNewAxisLabel,
        fontSize: "12px",
        fontFamily: HVTheme.fontFamily,
        align: "right",
        hideOverlap: false,
        formatter:
          viewType === "wait_time" || viewType === "waittime"
            ? "{value}s"
            : (value) => {
                const [inNumber, inSymbol] = numberSymbol(value);
                return `${inNumber}${inSymbol}`;
              }
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.chart.splitLineColor,
          width: 1
        }
      }
    },
    series: generateSeries()
  };
};

const renderedTitle = (color: string, name: string) => {
  return renderToString(
    <ToolTipTitle>
      <ColorDot color={color} />
      {name}
    </ToolTipTitle>
  );
};

export const chartRetailObjectType = (
  theme: Theme,
  timeInterval: TimeInterval,
  activeLocations: Set<string>,
  baseTime: BaseTimeProps,
  analyticsData: AnalyticQueueDataResponse[],
  translate: (key: string) => string
): EChartsOption => {
  const { mode } = baseTime;

  const generateSeries = analyticsData.reduce<SeriesOption[]>((acc, item) => {
    if (!activeLocations.has(item.key)) {
      const [avgTime] = numberSymbol(item.averageDwellTime as number);
      const [avgCount] = numberSymbol(item.averageQueueCount as number);
      acc.push({
        name: item.locationName,
        type: "scatter",
        data: [[avgTime, avgCount]],
        color: item.color
      });
    }
    return acc;
  }, []);

  return {
    tooltip: {
      confine: true,
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (!isArray(params)) {
          return chartTooltipFormatter(
            timeInterval,
            renderedTitle(params.color as string, params.seriesName as string),
            [
              {
                label: translate("count"),
                data: String((params.data as number[])[1]),
                isInteger: false
              },
              {
                label: translate("staying_time_s"),
                data: formatTime((params.data as number[])[0], true),
                isInteger: true
              }
            ],
            "",
            mode,
            1
          );
        }

        return "";
      },
      ...commonTooltipSettings()
    },
    legend: {
      show: false
    },
    grid: {
      left: 32,
      right: 15,
      top: 20,
      bottom: 20,
      containLabel: true,
      show: true,
      borderColor: "transparent"
    },
    toolbox: {
      show: false
    },
    dataZoom: [
      {
        type: "inside"
      }
    ],
    xAxis: [
      {
        type: "value",
        name: translate("avg_staying_time"),
        nameLocation: "middle",
        nameGap: 25,
        nameTextStyle: {
          fontFamily: HVTheme.fontFamily,
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 12,
          color: theme.chart.colorNewText
        },
        axisLabel: {
          color: theme.chart.colorAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          formatter: (value) => (value < 10 ? `0${value}` : `${value}`)
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.chart.splitLineColor
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.chart.splitLineColor
          }
        }
      }
    ],
    yAxis: [
      {
        type: "value",
        name: translate("average_count"),
        nameLocation: "middle",
        nameGap: 35,
        nameTextStyle: {
          fontFamily: HVTheme.fontFamily,
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 12,
          color: theme.chart.colorNewText
        },
        axisLabel: {
          color: theme.chart.colorAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.chart.splitLineColor
          }
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: theme.chart.splitLineColor
          }
        },
        axisTick: {
          show: false
        }
      }
    ],
    series: generateSeries
  };
};

export const combinedPieAndLineChart = (
  theme: Theme,
  timeInterval: TimeInterval,
  activeLocations: Set<string>,
  baseTime: BaseTimeProps,
  viewType: string,
  analyticsData: AnalyticsDataResponse[],
  timeZone?: string,
  analyticsFromReport = false
): EChartsOption => {
  const { mode, start, end } = baseTime;
  const result = getAxisIntervalLabels(
    mode,
    start,
    end,
    timeInterval,
    timeZone
  );
  const { labels: xAxisLabels } = result;

  const activeData = analyticsData.filter(
    (item) => !activeLocations.has(item.key)
  );

  const generateSeries = (): SeriesOption[] =>
    activeData.map((item) => ({
      name: item.locationName,
      type: "line",
      showSymbol: item.chartData.length === 1,
      seriesLayoutBy: "row",
      emphasis: { focus: "series" },
      data: item.chartData,
      color: item.color
    }));

  return {
    color: activeData.map(({ color }) => color),

    tooltip: tooltipSettingLine(timeInterval, mode),
    legend: {
      show: false
    },
    grid: {
      top: 20,
      bottom: 20,
      left: "55%",
      right: 15
    },
    toolbox: {
      show: false
    },
    dataZoom: [
      {
        type: "inside"
      }
    ],
    xAxis: [
      {
        type: "category",
        nameTextStyle: {
          color: theme.chart.colorNewAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          verticalAlign: "top",
          lineHeight: 25,
          padding: [1, 0, 0, 0]
        },
        nameGap: 11,
        data: xAxisLabels,
        boundaryGap: false,
        axisLabel: {
          color: theme.chart.colorNewAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          formatter: xAxisLabelFormatter(timeInterval, mode)
        },
        axisLine: {
          show: false
        },
        axisTick: {
          lineStyle: {
            color: theme.widget.stoppedVehicleStatistics.graphLineColor,
            width: 1
          }
        }
      }
    ],
    yAxis: {
      gridIndex: 0,
      type: "value",
      nameTextStyle: {
        color: theme.chart.colorNewAxisLabel,
        fontSize: "12px",
        fontFamily: HVTheme.fontFamily,
        align: "right",
        padding: [0, 6, 0, 0]
      },
      nameGap: 12,
      splitNumber: 2,
      axisLabel: {
        color: theme.chart.colorNewAxisLabel,
        fontSize: "12px",
        fontFamily: HVTheme.fontFamily,
        align: "right",
        hideOverlap: false,
        formatter:
          viewType === "wait_time" || viewType === "waittime"
            ? "{value}s"
            : (value) => {
                const [inNumber, inSymbol] = numberSymbol(value);
                return `${inNumber}${inSymbol}`;
              }
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: theme.chart.splitLineColor,
          width: 1
        }
      }
    },
    series: [
      ...(generateSeries() as SeriesOption[]),
      {
        type: "pie",
        id: "pie",
        startAngle: 135,
        radius: ["68px", "100px"],
        center: ["25%", "50%"],
        emphasis: {
          focus: "self"
        },
        itemStyle: {
          borderColor: theme.chart.pieChartBorder,
          borderWidth: 1
        },
        label: {
          formatter: ({ name, value, percent }) => {
            const [inNumber, inSymbol] = numberSymbol(value as number);
            if (analyticsFromReport) {
              return `${name.substring(0, 10)}${name.length > 10 ? "..." : ""}`;
            }
            return `${name.substring(0, 20)}${
              name.length > 20 ? "..." : ""
            }: ${inNumber}${inSymbol} (${percent}%)`;
          },
          color: theme.chart.colorSecondaryAxisLabel,
          fontSize: "12px",
          fontFamily: HVTheme.fontFamily,
          lineHeight: 25
        },
        encode: {
          itemName: "location",
          value: "total",
          tooltip: "total"
        },
        data: activeData.map((item) => ({
          name: item.locationName,
          value: item.total,
          itemStyle: {
            color: item.color
          }
        }))
      }
    ]
  };
};
